import { GridCellProps, GridColumnProps, GridPageChangeEvent } from "@progress/kendo-react-grid";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TableModel, Column } from "../../models/TableModel";
import { ChartModel } from "../../models/ChartModel";
import DataGrid from "../controls/DataGrid";
import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartTitle,
  ChartLegend,
  ChartValueAxis,
  ChartValueAxisItem,
} from "@progress/kendo-react-charts";
import { Constants } from "../../models/Constants";
import i18next from "../../i18n";

type TablesWithChartsProps = {
  tables: Array<TableModel> | undefined;
  charts: Array<ChartModel> | undefined;
  noRecordsText?: string;
};

const DATA_ITEM_KEY = "id";
const SELECTED_FIELD = "selected";
const DEFAULT_PAGE_SIZE = 20;

// Average pixel width of one horizontal legend item (icon + district name label).
// District names like "Balochistan|Awaran" are long — 180px is a safe estimate.
const AVG_LEGEND_ITEM_WIDTH_PX = 180;
// Height of one legend row in pixels
const LEGEND_ROW_HEIGHT_PX = 24;
// Fixed height reserved for the plot area itself (axes, lines, title)
const PLOT_AREA_HEIGHT_PX = 400;

/**
 * Calculates the total chart height needed to show the full built-in legend
 * plus the plot area, based on the number of series and the chart width.
 */
const calcChartHeight = (seriesCount: number, chartWidth: number): number => {
  if (seriesCount === 0) return PLOT_AREA_HEIGHT_PX;
  const itemsPerRow = Math.max(1, Math.floor(chartWidth / AVG_LEGEND_ITEM_WIDTH_PX));
  const legendRows = Math.ceil(seriesCount / itemsPerRow);
  const legendHeight = legendRows * LEGEND_ROW_HEIGHT_PX + 40; // 40px buffer for legend padding
  return PLOT_AREA_HEIGHT_PX + legendHeight;
};

// Standalone column def builder — defined outside component to avoid recreation on every render
const buildColDefs = (
  columns: Array<Column>,
  t: (key: string) => string
): Array<GridColumnProps> => {
  const healthFacilityTypeKey = t("translation:DataQualityAnalysis:HealthFacilityType");
  const healthFacilityNameKey = t("translation:DataQualityAnalysis:HealthFacilityName");
  const percentageKey = t("translation:DataQualityAnalysis:Percentage");

  const getValueByKey = (dataItem: any, key: string) => {
    if (dataItem[key] !== undefined) return dataItem[key];
    const lowerKey = key.toLowerCase();
    for (const itemKey of Object.keys(dataItem)) {
      if (itemKey.toLowerCase() === lowerKey) return dataItem[itemKey];
    }
    return undefined;
  };

  return columns?.map((row: Column, index: number) => {
    let key = row.key;

    if (key === Constants.Common.iptp) key = Constants.Common.iptpColumnfield;
    if (key === Constants.Common.tpig) key = Constants.Common.tpigColumnfield;
    if (key.toLocaleLowerCase() === healthFacilityTypeKey.toLocaleLowerCase()) key = Constants.Common.healthFacilityType;
    if (key.toLocaleLowerCase() === healthFacilityNameKey.toLocaleLowerCase()) key = Constants.Common.healthFacilityName;
    if (key.toLocaleLowerCase() === percentageKey.toLocaleLowerCase()) key = Constants.Common.Percentage;

    return {
      title: row.label,
      width: `${row.width}px`,
      filterable: false,
      locked: index === 0,
      cells: {
        data: (props: GridCellProps) => {
          const cellValue = getValueByKey(props.dataItem, key);
          return (
            <td
              title={cellValue}
              style={props.style}
              className={props.className}
              colSpan={props.colSpan}
              role={"gridcell"}
              aria-colindex={props.ariaColumnIndex}
              aria-selected={props.isSelected}
            >
              {cellValue}
            </td>
          );
        },
      },
    } as GridColumnProps;
  }) ?? [];
};

type TableWithPaginationProps = {
  table: TableModel;
  colClass: string;
  t: (key: string) => string;
  noRecordsText?: string;
};

/** Renders a single paginated table. Memoized so it only re-renders when its own data changes. */
const TableWithPagination = React.memo(({ table, colClass, t, noRecordsText }: TableWithPaginationProps) => {
  const [page, setPage] = useState({ skip: 0, take: DEFAULT_PAGE_SIZE });

  const colDefs = useMemo(() => buildColDefs(table.columns, t), [table.columns, t]);

  const pagedData = useMemo(
    () => table.rows?.slice(page.skip, page.skip + page.take) ?? [],
    [table.rows, page.skip, page.take]
  );

  const handlePageChange = useCallback((e: GridPageChangeEvent) => {
    setPage({ skip: e.page.skip, take: e.page.take });
  }, []);

  return (
    <div className={colClass}>
      <p>
        <b>{table.title}</b>
      </p>
      <DataGrid
        className="k-grid k-grid-wrapper k-grid-action-wrapper"
        columns={colDefs}
        data={pagedData}
        dataItemKey={DATA_ITEM_KEY}
        selectedField={SELECTED_FIELD}
        hasActionBtn={true}
        filterable={false}
        pageable={true}
        scrollable="scrollable"
        total={table.rows?.length ?? 0}
        skip={page.skip}
        take={page.take}
        onPageChange={handlePageChange}
        selectable={{
          enabled: true,
          drag: false,
          cell: true,
          mode: "single",
        }}
        noRecordsText={noRecordsText}
      />
    </div>
  );
});

/** Renders tabular and chart component */
const TablesWithCharts = React.memo((props: TablesWithChartsProps) => {
  const { t } = useTranslation();
  const { tables, charts, noRecordsText } = props;

  const yAxisTitle = i18next.t("DataQualityAnalysis.Percentage") + " %";
  const xAxisTitle = i18next.t("DataQualityAnalysis.Year");

  const renderTableComponent = () =>
    tables?.map((table: TableModel, index: number) => (
      <TableWithPagination
        key={index}
        table={table}
        colClass={tables.length > 1 ? "col-sm-6" : "col-sm-12"}
        t={t}
        noRecordsText={noRecordsText}
      />
    ));

  const renderGraphComponent = () =>
    charts?.map((chart: ChartModel, idx: number) => {
      const categoryCount = chart.categories?.length ?? 0;
      const seriesCount = chart.values?.length ?? 0;

      // Width driven by X-axis category count (years / districts on X-axis)
      const chartWidth = Math.max(600, categoryCount * 60);
      // Height grows to fit the built-in legend fully — no clipping, full interactivity preserved
      const chartHeight = calcChartHeight(seriesCount, chartWidth);

      return (
        <div key={idx} className={charts.length > 1 ? "col-sm-6" : "col-sm-12"}>
          {/* Scrollable wrapper so wide charts don't overflow the page */}
          <div style={{ overflowX: "auto", width: "100%" }}>
            <Chart style={{ height: chartHeight, width: chartWidth, minWidth: "100%" }}>
              <ChartTitle text={chart?.title} />
              <ChartLegend position='top' orientation='horizontal' />
              <ChartValueAxis>
                <ChartValueAxisItem
                  title={{ text: yAxisTitle }}
                  labels={{}}
                  type='numeric'
                />
              </ChartValueAxis>
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  title={{ text: xAxisTitle }}
                  categories={chart.categories}
                  startAngle={45}
                />
              </ChartCategoryAxis>
              <ChartSeries>
                {chart?.values?.map((item: any, i: number) => (
                  <ChartSeriesItem
                    key={i}
                    type='line'
                    tooltip={{ visible: true }}
                    data={item.values}
                    name={item.key}
                  />
                ))}
              </ChartSeries>
            </Chart>
          </div>
        </div>
      );
    });

  return (
    <>
      <div className='row'>{renderTableComponent()}</div>
      {charts && <div className='row'>{renderGraphComponent()}</div>}
    </>
  );
});

export default TablesWithCharts;
